define('util/NavBuilder',[
    'lib/knockout',
    'lib/lodash4',
    'services/featureToggleService',
    'extensions/i18n/knockout.i18n'
], function (
    ko,
    _,
    featureToggleService
) {
    'use strict';

    function createNavItem(textKey, href, key) {
        var navItem = {
            textKey: textKey,
            text: ''
        };

        if (href) {
            navItem.href = href;
        }

        if (key) {
            navItem.key = key;
        }

        if (ko.i18n.keyExists(textKey)) {
            navItem.text = ko.i18n.renderString(textKey);
        }

        return navItem;
    }

    // Binds private state to methods from a prototype-like object
    function extend(object, state, methods) {
        var mappedMethods = _.mapValues(methods, function (method) {
            return _.bind(method, object, state);
        });
        _.assign(object, mappedMethods);
    }

    function featurePredicate(featureName) {
        var feature = featureToggleService.getFeatureToggle(featureName);
        return ko.unwrap(feature);
    }

    //=====================
    // EntryBuilder
    //=====================

    var entryBuilderMethods = {
        setKey: function (state, key) {
            state.entry.key = key;
            return this;
        },
        requireAuth: function (state) {
            var auths = Array.prototype.slice.call(arguments, 1);
            state.entry.roles = auths;
            return this;
        },
        requireFeatures: function (state) {
            var featureNames = Array.prototype.slice.call(arguments, 1);
            state.entry.enabled = ko.computed(function () {
                return _.every(featureNames, featurePredicate);
            });
            return this;
        },
        requireAnyFeatures: function(state) {
            var featureNames = Array.prototype.slice.call(arguments, 1);
            state.entry.enabled = ko.computed(function () {
                return _.some(featureNames, featurePredicate);
            });
            return this;
        },
        requireStandaloneApp: function (state) {
            state.entry.enabled = ko.computed(function () {
                return false;
            });
            return this;
        },
        notWithFeatures: function (state) {
            var featureNames = Array.prototype.slice.call(arguments, 1);
            state.entry.enabled = ko.computed(function () {
                return !_.some(featureNames, featurePredicate);
            });
            return this;
        },
        newWindow: function (state, target) {
            state.entry.target = target;
            return this;
        },
        subLink: function (state, textKey, href) {
            state.entry.subLinks = state.entry.subLinks || [];
            var navItem = createNavItem(textKey, href);
            state.entry.subLinks.push(navItem);
            return new SubLinkBuilder(state.navBuilder, this, navItem);
        },
        subEntry: function (state, textKey, href) {
            state.entry.subLinks = state.entry.subLinks || [];
            var navItem = createNavItem(textKey, href);
            state.entry.subLinks.push(navItem);
            return new EntryBuilder(this, navItem);
        },
        entry: function (state, textKey, href) {
            return state.navBuilder.entry(textKey, href);
        },
        placeholderFor: function (state, name) {
            return state.navBuilder.placeholderFor(name);
        }
    };

    function EntryBuilder(navBuilder, entry) {
        var privateState = {
            navBuilder: navBuilder,
            entry: entry
        };
        extend(this, privateState, entryBuilderMethods);
    }

    //=====================
    // SubLinkBuilder
    //=====================

    var subLinkBuilderMethods = _.assign({}, entryBuilderMethods, {
        subLink: function (state, textKey, href) {
            return state.entryBuilder.subLink(textKey, href);
        }
    });

    function SubLinkBuilder(navBuilder, entryBuilder, entry) {
        var privateState = {
            navBuilder: navBuilder,
            entryBuilder: entryBuilder,
            entry: entry
        };
        extend(this, privateState, subLinkBuilderMethods);
    }

    // ====================
    // NavBuilder
    // ====================

    function isEnabled(item) {
        return item.enabled ? item.enabled() : true;
    }

    function withoutEnabledFlag(item) {
        return _.reduce(item, function (newItem, value, key) {
            if (key !== 'enabled') {
                newItem[key] = value;
            }
            return newItem;
        }, {});
    }

    function cleanDisabled(items) {
        return _(items)
            .filter(isEnabled)
            .map(withoutEnabledFlag)
            .valueOf();
    }

    function cleanSubLinks(entries) {
        _.each(entries, function (entry) {
            if (entry.subLinks) {
                entry.subLinks = cleanDisabled(entry.subLinks);
                cleanSubLinks(entry.subLinks);
            }
        });

        return entries;
    }

    var navBuilderMethods = {
        entry: function (state, textKey, href) {
            var navItem = createNavItem(textKey, href);
            state.navItems.push(navItem);
            return new EntryBuilder(this, navItem);
        },
        placeholderFor: function (state, name) {
            state.navItems.push({
                placeholder: name
            });
            return this;
        },
        registerEntries: function(state, name, navStructure, navParent) {
            _.forEach(navStructure, function (navEntry) {
                navEntry.fromPlaceholder = name;
                navEntry.text = ko.i18n.renderString(navEntry.textKey);

                if (navEntry.subLinks) {
                    _.forEach(navEntry.subLinks, function (subNavEntry) {
                        subNavEntry.text = ko.i18n.renderString(subNavEntry.textKey);

                        if (subNavEntry.subLinks) {
                            _.forEach(subNavEntry.subLinks, function (subNavEntry) {
                                subNavEntry.text = ko.i18n.renderString(subNavEntry.textKey);
                            });
                        }
                    });
                }
            });

            if (navParent) {
                var parentEntry = _.find(state.navItems(), function (item) {
                    return item.key === navParent;
                });

                if (parentEntry) {
                    _.each(navStructure, function (navEntry) {
                        var target;
                        if (parentEntry.key === navEntry.key) {
                            target = parentEntry;
                        } else if (parentEntry.subLinks) {
                            var parentPath = _.find(parentEntry.subLinks, function (subEntry) {
                                return subEntry.key === navEntry.key;
                            });

                            if (parentPath) {
                                target = parentPath;
                            }
                        }

                        if (target && target.subLinks) {
                            if (navEntry.subLinks) {
                                _.each(navEntry.subLinks, function (subEntry) {
                                    subEntry.fromPlaceholder = name;
                                    var existingEntryIdx = _.findIndex(target.subLinks, function (item) {
                                        return item.key === subEntry.key;
                                    });
                                    if (existingEntryIdx >= 0) {
                                        target.subLinks.splice.apply(target.subLinks, [existingEntryIdx, 1].concat(subEntry));
                                    } else {
                                        target.subLinks.push(subEntry);
                                    }
                                });
                            } else {
                                var existingEntryIdx = _.findIndex(target.subLinks, function (item) {
                                    return item.key === navEntry.key;
                                });
                                if (existingEntryIdx >= 0) {
                                    target.subLinks.splice.apply(target.subLinks, [existingEntryIdx, 1].concat(navEntry));
                                } else {
                                    target.subLinks.push(navEntry);
                                }
                            }

                            state.navItems.valueHasMutated();
                        }
                    });
                } else {
                    return;
                }
            } else {
                var spliceIdx = _.findIndex(state.navItems(), function (item) {
                    return item.placeholder === name || item.fromPlaceholder === name;
                });

                if (spliceIdx >= 0) {
                    var lastIdx, spliceCnt = 1;

                    lastIdx = _.findLastIndex(state.navItems(), function (item) {
                        return item.fromPlaceholder === name;
                    });

                    if (lastIdx > spliceIdx) {
                        spliceCnt += lastIdx - spliceIdx;
                    }

                    state.navItems.splice.apply(state.navItems, [spliceIdx, spliceCnt].concat(navStructure));
                }
            }
        }
    };

    function NavBuilder() {
        var privateState = {
            navItems: ko.observableArray([]),
            placeholderMap: {}
        };
        extend(this, privateState, navBuilderMethods);

        this.navTree = ko.pureComputed(function () {
            var entries = cleanDisabled(privateState.navItems());
            entries = _.filter(entries, function(entry) {
                return !_.has(entry, 'placeholder');
            });
            cleanSubLinks(entries);

            return entries;
        }, this).extend({ rateLimit: 500, method: "notifyWhenChangesStop" });
    }


    return NavBuilder;
});

